import { useEffect, useState } from "react";
import { Package, Trash2, PlusCircle, Save, X, Layers } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";

export default function PurchaseForm({ onSaved, onCancel }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [fechaCompra, setFechaCompra] = useState("");
  const [numeroFactura, setNumeroFactura] = useState("");
  const [details, setDetails] = useState([]);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data?.data || res.data || []);
    } catch {
      setSuppliers([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data?.data || res.data || []);
    } catch {
      setProducts([]);
    }
  };

  /* =========================
     DETALLES
  ========================== */
  const addDetail = () => {
    setDetails([
      ...details,
      { 
        product_id: "", 
        presentation_id: null, // 🆕 NUEVO
        cantidad: 1, 
        precio_unitario: 0,
        productData: null // 🆕 Para almacenar info del producto seleccionado
      },
    ]);
  };

  const updateDetail = (index, field, value) => {
    const updated = [...details];
    updated[index][field] = value;
    
    // 🆕 Si cambió el producto, cargar sus presentaciones
    if (field === "product_id") {
      const selectedProduct = products.find(p => p.id == value);
      updated[index].productData = selectedProduct;
      updated[index].presentation_id = null; // Reset presentación
    }
    
    setDetails(updated);
  };

  const removeDetail = (index) => {
    setDetails(details.filter((_, i) => i !== index));
  };

  /* =========================
     🆕 HELPERS PARA PRESENTACIONES
  ========================== */
  const tienePresentaciones = (detail) => {
    return detail.productData?.presentations && detail.productData.presentations.length > 0;
  };

  // 🆕 NUEVO: Solo mostrar selector si tiene presentaciones VARIANTE
  const tienePresentacionesVariante = (detail) => {
    if (!detail.productData?.presentations) return false;
    return detail.productData.presentations.some(p => p.tipo_presentacion === 'variante');
  };

  const getPresentacionNombre = (detail) => {
    if (!detail.presentation_id || !detail.productData?.presentations) {
      return "";
    }
    const pres = detail.productData.presentations.find(p => p.id == detail.presentation_id);
    return pres ? pres.descripcion : "";
  };

  const getNombreCompleto = (detail) => {
    const producto = products.find(p => p.id == detail.product_id);
    if (!producto) return "";
    
    const presNombre = getPresentacionNombre(detail);
    return presNombre ? `${producto.nombre} - ${presNombre}` : producto.nombre;
  };

  /* =========================
     CÁLCULOS DE SUBTOTAL POR PRODUCTO
  ========================== */
  const calcularSubtotalLinea = (detail) => {
    const precioUnitario = Number(detail.precio_unitario || 0);
    const cantidad = Number(detail.cantidad || 0);
    return precioUnitario * cantidad;
  };

  /* =========================
     TOTALES GENERALES (CON IVA 13%)
  ========================== */
  const subtotalGeneral = details.reduce(
    (sum, d) => sum + calcularSubtotalLinea(d),
    0
  );

  const ivaTotal = subtotalGeneral * 0.13;
  const montoFinal = subtotalGeneral + ivaTotal;

  /* =========================
     ACTIONS
  ========================== */
  const resetForm = () => {
    setSupplierId("");
    setFechaCompra("");
    setNumeroFactura("");
    setDetails([]);
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierId || !fechaCompra || !numeroFactura || details.length === 0) {
      Swal.fire("Error", "Complete todos los campos", "error");
      return;
    }

    // 🆕 Validar que productos con presentaciones VARIANTE tengan una seleccionada
    for (let i = 0; i < details.length; i++) {
      const detail = details[i];
      if (tienePresentacionesVariante(detail) && !detail.presentation_id) {
        Swal.fire(
          "Error",
          `Debe seleccionar una presentación (sabor/variante) para ${detail.productData.nombre}`,
          "error"
        );
        return;
      }
    }

    const payload = {
      supplier_id: supplierId,
      fecha_compra: fechaCompra,
      numero_factura_proveedor: numeroFactura,
      details: details.map((d) => ({
        product_id: d.product_id,
        presentation_id: d.presentation_id || null, // 🆕 NUEVO
        cantidad: Number(d.cantidad),
        precio_unitario: Number(d.precio_unitario),
      })),
    };

    try {
      await api.post("/purchases", payload);
      Swal.fire("Guardado", "Compra registrada correctamente", "success");
      resetForm();
      onSaved?.();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al guardar la compra",
        "error"
      );
    }
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Package size={24} color="#667eea" />
          <h2 className="card-title">Nueva Compra</h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* DATOS GENERALES */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px"
          }} className="mb-4">
            <div className="form-group">
              <label className="form-label">Proveedor *</label>
              <select
                className="form-control"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
              >
                <option value="">Seleccione un proveedor</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fecha de Compra *</label>
              <input
                type="date"
                className="form-control"
                value={fechaCompra}
                onChange={(e) => setFechaCompra(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">N° Factura Proveedor *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: FAC-001-2025"
                value={numeroFactura}
                onChange={(e) => setNumeroFactura(e.target.value)}
                required
              />
            </div>
          </div>

          {/* SEPARADOR */}
          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {/* PRODUCTOS */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 style={{ fontSize: "16px", fontWeight: 600, margin: 0 }}>
                Productos
              </h4>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={addDetail}
              >
                <PlusCircle size={16} />
                Agregar Producto
              </button>
            </div>

            {details.length === 0 ? (
              <div className="alert alert-info text-center">
                No hay productos agregados. Haz clic en "Agregar Producto" para comenzar.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {details.map((d, index) => (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: tienePresentacionesVariante(d) 
                        ? "2fr 1.5fr 1fr 1fr 1fr 60px" 
                        : "2fr 1fr 1fr 1fr 60px",
                      gap: "12px",
                      padding: "12px",
                      background: "#f7fafc",
                      borderRadius: "8px",
                      alignItems: "end",
                    }}
                  >
                    {/* PRODUCTO */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Producto *
                      </label>
                      <select
                        className="form-control"
                        value={d.product_id}
                        onChange={(e) =>
                          updateDetail(index, "product_id", e.target.value)
                        }
                        required
                      >
                        <option value="">Seleccionar</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre}
                            {p.presentations?.some(pr => pr.tipo_presentacion === 'variante') && " 🎨"}
                            {p.presentations?.some(pr => pr.tipo_presentacion === 'empaque') && " 📦"}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 🆕 PRESENTACIÓN (solo si el producto tiene presentaciones VARIANTE) */}
                    {tienePresentacionesVariante(d) && (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                          <Layers size={12} />
                          Presentación *
                        </label>
                        <select
                          className="form-control"
                          value={d.presentation_id || ""}
                          onChange={(e) =>
                            updateDetail(index, "presentation_id", e.target.value)
                          }
                          required
                          style={{ borderColor: "#805ad5" }}
                        >
                          <option value="">Seleccionar variante</option>
                          {d.productData.presentations
                            .filter(pres => pres.tipo_presentacion === 'variante')
                            .map((pres) => (
                              <option key={pres.id} value={pres.id}>
                                {pres.descripcion}
                                {` (Stock: ${pres.stock_actual || 0})`}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                    {/* CANTIDAD */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Cantidad *
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        value={d.cantidad}
                        onChange={(e) =>
                          updateDetail(index, "cantidad", e.target.value)
                        }
                        required
                      />
                    </div>

                    {/* PRECIO UNITARIO */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Precio Unitario (sin IVA) *
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={d.precio_unitario}
                        onChange={(e) =>
                          updateDetail(index, "precio_unitario", e.target.value)
                        }
                        required
                      />
                    </div>

                    {/* SUBTOTAL */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Subtotal
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={`$${calcularSubtotalLinea(d).toFixed(2)}`}
                        disabled
                        style={{ 
                          background: "#e2e8f0",
                          fontWeight: 600,
                          color: "#2d3748"
                        }}
                      />
                    </div>

                    {/* ELIMINAR */}
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeDetail(index)}
                      style={{ height: "46px" }}
                      title="Eliminar producto"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SEPARADOR */}
          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {/* TOTALES */}
          <div style={{
            background: "#f7fafc",
            padding: "20px",
            borderRadius: "10px"
          }} className="mb-4">
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: "12px",
              maxWidth: "400px",
              marginLeft: "auto"
            }}>
              {/* Subtotal */}
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "16px", fontWeight: 500 }}>
                  Subtotal (sin IVA):
                </span>
                <span style={{ fontSize: "16px", fontWeight: 600 }}>
                  ${subtotalGeneral.toFixed(2)}
                </span>
              </div>

              {/* IVA */}
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "16px", fontWeight: 500 }}>
                  IVA (13%):
                </span>
                <span style={{ fontSize: "16px", fontWeight: 600 }}>
                  ${ivaTotal.toFixed(2)}
                </span>
              </div>

              {/* Línea divisoria */}
              <div style={{ borderTop: "2px solid #cbd5e0", margin: "4px 0" }}></div>

              {/* Total */}
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "18px", fontWeight: 700 }}>
                  Total de la Compra:
                </span>
                <span style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#48bb78"
                }}>
                  ${montoFinal.toFixed(2)}
                </span>
              </div>
            </div>

            <small style={{ 
              color: "#718096", 
              display: "block", 
              marginTop: "16px",
              fontStyle: "italic"
            }}>
              * Los precios unitarios deben ingresarse sin IVA según factura de crédito fiscal
            </small>
          </div>

          {/* BOTONES */}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              Guardar Compra
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
            >
              <X size={18} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}